import React, { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Copy, Check } from "lucide-react";
import { useTranslation } from "react-i18next";

type CopyableLinkInputProps = {
  value: string;
  className?: string;
  inputClassName?: string;
  buttonAriaLabel?: string;
  label?: React.ReactNode;
  onCopied?: () => void;
};

export function CopyableLinkInput({
  value,
  className,
  inputClassName,
  buttonAriaLabel = "Copy to clipboard",
  label,
  onCopied,
}: CopyableLinkInputProps) {
  const [copied, setCopied] = useState<boolean>(false);
  const timeoutRef = useRef<number | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    };
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      onCopied?.();
      timeoutRef.current = window.setTimeout(() => setCopied(false), 1500);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = value;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        setCopied(true);
        onCopied?.();
        timeoutRef.current = window.setTimeout(() => setCopied(false), 1500);
      } finally {
        document.body.removeChild(ta);
      }
    }
  };

  return (
    <div className={cn("space-y-2", className)}>
      {label ? (
        <div className="text-sm font-medium leading-none">{label}</div>
      ) : null}

      <div className="relative">
        <Input
          value={value}
          readOnly
          className={cn("pe-10 font-bold text-base", inputClassName)}
          placeholder={t("generated_link")}
        />

        <div className="absolute inset-y-0 end-0 flex items-center pe-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="link"
                size="icon"
                className="h-8 w-8"
                aria-label={buttonAriaLabel}
                onClick={copy}
              >
                {copied ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">
              {copied ? t("copied") + "!" : t("copy")}
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}
